import { UserRole } from '../../../core/enum';
import { HelpersInterface } from '../../interfaces';
import { DropdownInterface } from '../../models';

export function compareCompaniesArray(
  companiesArray: HelpersInterface.ICompanyFilterItem[],
  inputIdsArray: number[], isAll: boolean): HelpersInterface.ICompanyFilterItem[] {
  let outputArray: HelpersInterface.ICompanyFilterItem[] = [];
  outputArray = companiesArray.map(company => {
    company.endpoints = company.endpoints.map(endpoint => {
      if (inputIdsArray.includes(endpoint.id)) {
        endpoint.checked = true;
        company.indeterminate = true;
      } else {
        endpoint.checked = false;
      }

      if (isAll) {
        endpoint.checked = true;
        endpoint.disabled = true;
      }

      if (inputIdsArray.length === 30 && !inputIdsArray.includes(endpoint.id)) {
        endpoint.disabled = true;
        company.disabled = true;
      }

      if (!isAll && !inputIdsArray.length) {
        endpoint.disabled = false;
      }

      return endpoint;
    });

    if (!inputIdsArray.length) {
      company.indeterminate = false;
    }

    if (company.endpoints.every(endpoint => !endpoint.checked)) {
      company.checked = false;
    } else if (company.endpoints.every(endpoint => endpoint.checked)) {
      company.checked = true;
      company.indeterminate = false;
    } else {
      company.checked = false;
    }

    if (company.endpoints.every(endpoint => endpoint.checked) && inputIdsArray.length === 30) {
      company.disabled = true;
      company.indeterminate = false;
      company.checked = true;
    }


    if (isAll) {
      company.checked = true;
      company.disabled = true;
    }

    if (!isAll && !inputIdsArray.length) {
      company.disabled = false;
    }

    return company;
  });

  return outputArray;
}

export function isOpenedCompanyState(
  inputCompaniesArray: HelpersInterface.ICompanyFilterItem[],
  inputIds: number[], isAll: boolean
): { [key: number]: boolean; } {
  let state: { [key: number]: boolean } = {};


  if (isAll) {
    inputCompaniesArray.forEach(company => {
      state[company.id] = true;
    });
  } else {
    inputCompaniesArray.forEach(company => {
      state[company.id] = company.endpoints.some((endpoint) => inputIds.includes(endpoint.id));
    });
  }

  return state;
}

export function compareEndpointsArray(
  endpointsArray: HelpersInterface.IEndpointFilterItem[],
  inputIdsArray: number[], isAll: boolean, userRole: UserRole): HelpersInterface.IEndpointFilterItem[] {
  let outputArray: HelpersInterface.IEndpointFilterItem[] = [];
  outputArray = endpointsArray.map(endpoint => {
    if (inputIdsArray.includes(endpoint.id)) {
      endpoint.checked = true;
    } else {
      endpoint.checked = false;
    }

    if (inputIdsArray.length === 30 && !inputIdsArray.includes(endpoint.id)) {
      endpoint.disabled = true;
    }

    if (!inputIdsArray.length) {
      endpoint.disabled = false;
    }

    if (isAll) {
      endpoint.checked = true;
      endpoint.disabled = true;
    }

    if (userRole === UserRole.viewer) {
      endpoint.disabled = true;
    }

    return endpoint;
  });

  return outputArray;
}

export function compareSourceSSPArray(
  companiesArray: DropdownInterface.ICompaniesDataGeneralSource[],
  inputIdsArray: number[],
  isAll: boolean,
  initialCheckedCompanies: number[]
): DropdownInterface.ICompaniesDataGeneralSource[] {
  let outputArray: DropdownInterface.ICompaniesDataGeneralSource[] = [];
  outputArray = companiesArray.map(company => {
    if (inputIdsArray.includes(company.id)) {
      company.checked = true;
    } else {
      company.checked = false;
    }

    if (isAll) {
      company.checked = true;
      company.disabled = true;
    }

    if (initialCheckedCompanies.includes(company.id)) {
      company.checked = false;
      company.disabled = true;
    }

    return company;
  });

  return outputArray;
}

export function rearrangeSourceSSPArray(inputArray: DropdownInterface.ICompaniesDataGeneralSource[], inputIdsArray: number[]):
  DropdownInterface.ICompaniesDataGeneralSource[] {
  let outputCompanies: DropdownInterface.ICompaniesDataGeneralSource[] = [];

  const selectedItems = inputArray.filter(item => inputIdsArray.includes(item.id));
  const unselectedItems = inputArray.filter(item => !inputIdsArray.includes(item.id));

  outputCompanies = [...selectedItems, ...unselectedItems];

  return outputCompanies;
}

export function rearrangeArrayEndpoints(inputArray: HelpersInterface.IEndpointFilterItem[], inputIdsArray: number[]):
  HelpersInterface.IEndpointFilterItem[] {
  let outputEndpoints: HelpersInterface.IEndpointFilterItem[] = [];

  const selectedItems = inputArray.filter(item => inputIdsArray.includes(item.id));
  const unselectedItems = inputArray.filter(item => !inputIdsArray.includes(item.id));

  outputEndpoints = [...selectedItems, ...unselectedItems];

  return outputEndpoints;
}

export function sortCompaniesArray(
  inputFilterListArray: HelpersInterface.ICompanyFilterItem[],
  inputIdsArray: number[]): HelpersInterface.ICompanyFilterItem[] {
  const inputIdsSet = new Set(inputIdsArray);

  inputFilterListArray.forEach((item) => {
    // Sort endpoints based on inputIdsArray order
    item.endpoints.sort((a, b) => {
      const aIdInInputIds = inputIdsSet.has(a.id);
      const bIdInInputIds = inputIdsSet.has(b.id);

      if (aIdInInputIds && !bIdInInputIds) {
        return -1;
      } else if (!aIdInInputIds && bIdInInputIds) {
        return 1;
      } else {
        const aIndex = inputIdsArray.indexOf(a.id);
        const bIndex = inputIdsArray.indexOf(b.id);
        return aIndex - bIndex;
      }
    });
  });

  // Sort inputFilterListArray based on whether any endpoint id is in inputIdsArray
  inputFilterListArray.sort((a, b) => {
    const aHasMatch = a.endpoints.some((endpoint) => inputIdsSet.has(endpoint.id));
    const bHasMatch = b.endpoints.some((endpoint) => inputIdsSet.has(endpoint.id));

    if (aHasMatch && !bHasMatch) {
      return -1;
    } else if (!aHasMatch && bHasMatch) {
      return 1;
    } else {
      return 0;
    }
  });

  return inputFilterListArray;
}

export function modifyEditCompanies(
  inputArray: HelpersInterface.ICompanyFilterItem[],
  inputIdsArray: number[],
  isAll: boolean,
): HelpersInterface.ICompanyFilterItem[] {
  let outputCompaniesArray: HelpersInterface.ICompanyFilterItem[] = [];

  const comparedCompaniesArray = compareCompaniesArray(inputArray, inputIdsArray, isAll);

  outputCompaniesArray = sortCompaniesArray(comparedCompaniesArray, inputIdsArray);
  return outputCompaniesArray;
}

export function compareEventsArray(
  itemsArray: HelpersInterface.IRules[],
  resArray: number[],
  isAll: boolean): HelpersInterface.IRules[] {
  itemsArray.forEach(item => {
    if (resArray.includes(item.id)) {
      item.checked = true;
    }

    if (isAll) {
      item.checked = true;
      item.disabled = true;
    } else {
      item.disabled = false;
    }
  });

  return itemsArray;
}
